import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { isAddress } from 'viem';
import { db } from '../firebase/firebaseCore';

interface DrawEntryResult {
  success: boolean;
  error?: string;
  transactionHash?: string;
  diagnostics?: any; // For debugging
}

const normalizeDrawEntryError = (error: any): string => {
  const message = String(error?.message || '').toLowerCase();

  if (error?.code === 4001 || message.includes('rejected by user') || message.includes('user rejected')) {
    return 'Transaction was rejected. Please confirm the transfer in your wallet.';
  }
  if (message.includes('insufficient funds') || message.includes('gas')) {
    return 'Insufficient balance to cover network fees for this transfer.';
  }
  if (message.includes('wallet_switchethereumchain') || message.includes('network switch')) {
    return 'Unable to switch to Base network. Please open your wallet and try again.';
  }
  if (
    message.includes('provider') ||
    message.includes('disconnected') ||
    message.includes('session') ||
    message.includes('login') ||
    message.includes('popup')
  ) {
    return 'Wallet session expired. Please sign in again and retry.';
  }
  return error?.message || 'Transaction failed';
};

export const handleDrawEntry = async (
  userEmail: string,
  rpc: any,
  recipientAddress: string,
  amount: string
): Promise<DrawEntryResult> => {
  if (!userEmail) {
    return { success: false, error: 'Invalid user email' };
  }

  if (!recipientAddress || !isAddress(recipientAddress)) {
    return { success: false, error: 'Invalid recipient address' };
  }

  // Validate amount is a positive number to avoid zero/dust tx
  const numericAmount = Number(amount);
  if (!isFinite(numericAmount) || numericAmount <= 0) {
    return { success: false, error: 'Invalid amount. Must be greater than 0.' };
  }

  const userRef = doc(db, 'users', userEmail);

  try {
    // Log current chain before send
    const currentChainId = await rpc.getChainId();
    console.log('Current chain ID before send:', currentChainId);

    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);

      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const userData = userDoc.data();

      if (!userData.enter_draw) {
        throw new Error('User is not eligible to enter draw');
      }

      if (userData.drawEntryInProgress) {
        throw new Error('Draw entry already in progress');
      }

      transaction.update(userRef, {
        drawEntryInProgress: true,
        drawEntryStartedAt: serverTimestamp(),
        enter_draw: false
      });

      return userData;
    });

    try {
      const response = await rpc.sendTransaction(
        recipientAddress,
        amount,
        undefined,
        (error: any) => {
          console.error('Detailed transaction error:', {
            message: error.message,
            code: error.code,
            data: error.data,
            stack: error.stack
          });
          throw error;
        }
      );

      await runTransaction(db, async (transaction) => {
        transaction.update(userRef, {
          drawEntryInProgress: false,
          lastDrawEntryAt: serverTimestamp(),
          enter_draw: false,
          asset: 'USDC'
        });
      });

      return {
        success: true,
        transactionHash: response.transactionHash || response.hash
      };

    } catch (txError: any) {
      // Reset the draw entry status
      await runTransaction(db, async (transaction) => {
        transaction.update(userRef, {
          drawEntryInProgress: false,
          enter_draw: true
        });
      });

      // Enhanced error diagnostics
      return {
        success: false,
        error: normalizeDrawEntryError(txError),
        diagnostics: {
          errorCode: txError.code,
          errorData: txError.data,
          chainId: await rpc.getChainId(),
          providerType: rpc.provider.constructor.name
        }
      };
    }

  } catch (error: any) {
    // Cleanup timer remains the same...
    setTimeout(async () => {
      try {
        await runTransaction(db, async (transaction) => {
          const doc = await transaction.get(userRef);
          if (doc.exists() && doc.data().drawEntryInProgress) {
            transaction.update(userRef, {
              drawEntryInProgress: false
            });
          }
        });
      } catch (cleanupError) {
        console.error('Error cleaning up draw entry state:', cleanupError);
      }
    }, 5 * 60 * 1000);

    return {
      success: false,
      error: normalizeDrawEntryError(error),
      diagnostics: {
        errorType: error.constructor.name,
        errorStack: error.stack,
        chainId: await rpc.getChainId().catch(() => 'unknown')
      }
    };
  }
};